import type { Account, Category, FinanceCategoryKind } from './types';

export type CategoryOption = {
  id: number;
  label: string;
  kind: FinanceCategoryKind;
  isActive: boolean;
};

function sortByOrder<T extends { sortOrder: number; name: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

/** Flattened `Parent - Child` options (parents first, then their children), for pickers. */
export function categoryOptions(categories: Category[]): CategoryOption[] {
  const parents = sortByOrder(categories.filter((c) => c.parentId == null));
  const childrenOf = (parentId: number) =>
    sortByOrder(categories.filter((c) => c.parentId === parentId));
  const options: CategoryOption[] = [];
  for (const parent of parents) {
    options.push({
      id: parent.id,
      label: parent.name,
      kind: parent.kind,
      isActive: parent.isActive,
    });
    for (const child of childrenOf(parent.id)) {
      options.push({
        id: child.id,
        label: `${parent.name} - ${child.name}`,
        kind: child.kind,
        isActive: child.isActive,
      });
    }
  }
  return options;
}

export function categoryLabelMap(categories: Category[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const option of categoryOptions(categories)) {
    map.set(option.id, option.label);
  }
  return map;
}

export function accountLabelMap(accounts: Account[]): Map<number, string> {
  return new Map(accounts.map((a) => [a.id, a.name]));
}

function accountLastFour(accountNumber: string | null | undefined): string | null {
  if (!accountNumber) return null;
  const digits = accountNumber.replace(/\D/g, '');
  if (digits.length === 0) return null;
  return digits.slice(-4);
}

/** `Name (Institution · 4921)` for account pickers. Missing parts are omitted. */
export function accountPickerLabel(account: {
  name: string;
  institution?: string | null;
  accountNumber?: string | null;
}): string {
  const extra = [account.institution?.trim(), accountLastFour(account.accountNumber)].filter(
    (part): part is string => Boolean(part),
  );
  if (extra.length === 0) return account.name;
  return `${account.name} (${extra.join(' · ')})`;
}

export type MoneyFormat = {
  /** Group digits with commas. */
  grouping?: boolean;
  /** Always show two decimal places. */
  decimals?: boolean;
};

/** A number with thousands separators and two decimals, unless a format option turns those off. */
export function formatMoney(value: number | null | undefined, format?: MoneyFormat): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const decimals = format?.decimals !== false;
  const digits = decimals ? 2 : 0;
  return value.toLocaleString('en-US', {
    useGrouping: format?.grouping !== false,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Read an amount typed with commas or spaces. */
export function parseMoneyInput(raw: string): number {
  return Number(raw.replace(/,/g, '').replace(/\s/g, '').replace(/[−–]/g, '-').trim());
}

/** USD with thousands separators, two decimals. */
export function formatUsd(value: number | null | undefined): string {
  return formatMoney(value);
}

export function formatAmount(value: number): string {
  return formatMoney(value);
}

export function roundUsd(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
